import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { Auth0Provider, useAuth0, AppState } from '@auth0/auth0-react';
import { callApi } from '../../lib/api';

// --- CONFIGURED WITH YOUR AUTH0 DETAILS ---
const AUTH0_DOMAIN = "codemineapp.us.auth0.com";
const AUTH0_CLIENT_ID = "gTh5IalRhW0n3kOjZPelCHPMa3BSrx61";
// This 'audience' is the Identifier you created for your API in the Auth0 dashboard.
const AUTH0_AUDIENCE = "https://api.codemineapp.com/";

// --- CRITICAL AUTH0 CONFIGURATION NOTE ---
// The `redirectUri` MUST match a URL you have added to your "Allowed Callback URLs"
// in your Auth0 Application settings. If you are developing locally, it might be
// 'http://localhost:8080'. If you have deployed your app, it will be your app's
// live URL (e.g., 'https://codemineapp.com' or 'https://your-app.aistudio.dev').
// The error "Callback URL mismatch" means the current URL of this app is not in the allowed list.
const redirectUri = window.location.origin;


interface Profile {
  plan: string;
  credits: number;
  role?: string;
  full_name?: string;
  name?: string;
  email?: string;
  android_pass_purchased?: boolean;
  ios_pass_purchased?: boolean;
  daily_tokens_used?: number;
  daily_token_limit?: number;
  monthly_tokens_used?: number;
  monthly_token_limit?: number;
  mobile_deployment_plan?: string;
}

interface GithubUser {
  login: string;
  name: string;
}

interface AuthContextType {
  // Auth0 properties
  isAuthenticated: boolean;
  user: any; // Auth0 user object
  isLoading: boolean;
  login: () => void;
  signup: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string>;

  // Application-specific properties
  profile: Profile | null;
  loadingProfile: boolean;
  refreshProfile: () => Promise<void>;
  updateUserPlan: (planName: string) => Promise<{ error: any | null }>;
  
  // Plan-based feature flags
  isStarter: boolean;
  isCreator: boolean;
  isPro: boolean;
  isBusiness: boolean;
  canDeploy: boolean;
  canUseCustomDomain: boolean;
  canDeployMobile: boolean;
  maxLiveProjects: number;

  // GitHub Integration
  isGithubConnected: boolean;
  githubUser: GithubUser | null;
  connectGithub: () => Promise<void>;
  disconnectGithub: () => void;
  createGithubRepo: (projectId: number, repoDetails: { name: string; description: string; isPrivate: boolean }) => Promise<{ url: string }>;
  pushToGithub: (projectId: number, files: { [fileName: string]: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProviderInternal: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { 
    isAuthenticated, 
    user, 
    isLoading, 
    loginWithRedirect, 
    logout: auth0Logout, 
    getAccessTokenSilently 
  } = useAuth0();
  
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // GitHub State
  const [isGithubConnected, setIsGithubConnected] = useState(false);
  const [githubUser, setGithubUser] = useState<GithubUser | null>(null);


  // --- GITHUB MOCK IMPLEMENTATION ---
  useEffect(() => {
    // Check localStorage to persist GitHub connection state
    try {
        const storedGithubUser = localStorage.getItem('githubUser');
        if (storedGithubUser) {
            setGithubUser(JSON.parse(storedGithubUser));
            setIsGithubConnected(true);
        }
    } catch (error) {
        console.error("Failed to parse GitHub user from localStorage:", error);
        localStorage.removeItem('githubUser'); // Clear corrupted data
    }
  }, []);

  const connectGithub = async () => {
    // This is a mock of the OAuth flow.
    console.log("Redirecting to GitHub for authorization...");
    return new Promise<void>(resolve => {
        setTimeout(() => {
            const mockUser = { login: 'github-user', name: 'Mock GitHub User' };
            localStorage.setItem('githubUser', JSON.stringify(mockUser));
            setGithubUser(mockUser);
            setIsGithubConnected(true);
            console.log("GitHub connected successfully (mocked).");
            resolve();
        }, 1500);
    });
  };

  const disconnectGithub = () => {
    localStorage.removeItem('githubUser');
    setGithubUser(null);
    setIsGithubConnected(false);
    console.log("GitHub disconnected.");
  };
  
  const createGithubRepo: AuthContextType['createGithubRepo'] = async (projectId, repoDetails) => {
    console.log(`MOCK: Creating GitHub repo for project ${projectId}`, repoDetails);
    return new Promise(resolve => {
        setTimeout(() => {
            const repoUrl = `https://github.com/${githubUser?.login || 'user'}/${repoDetails.name}`;
            console.log(`MOCK: Repo created at ${repoUrl}`);
            resolve({ url: repoUrl });
        }, 2000);
    });
  };

  const pushToGithub: AuthContextType['pushToGithub'] = async (projectId, files) => {
     console.log(`MOCK: Pushing ${Object.keys(files).length} files to GitHub for project ${projectId}`);
     return new Promise(resolve => {
        setTimeout(() => {
            console.log("MOCK: Push to GitHub successful.");
            resolve();
        }, 2500);
    });
  };


  const getAccessToken = useCallback((): Promise<string> => {
    // This call inherits its configuration from the Auth0Provider,
    // ensuring the correct audience and scopes ('openid profile email') are used.
    return getAccessTokenSilently();
  }, [getAccessTokenSilently]);
  
  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setProfile(null);
      setLoadingProfile(false);
      return;
    }
    
    setLoadingProfile(true);
    try {
      const accessToken = await getAccessToken();
      // FIX: Pass access token in Authorization header via options object.
      const profileData = await callApi('/user-profile', {
          headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (profileData && profileData.success && profileData.user) {
        setProfile(profileData.user);
      } else {
        setProfile(null);
      }
    } catch (error: any) {
    // Self-healing for 404: if the profile doesn't exist, create it.
    if (error.message && (error.message.includes('404') || error.message.toLowerCase().includes('not found'))) {
      console.warn("User profile not found, attempting to create one...");
      try {
        const accessToken = await getAccessToken();
        // FIX: Combine access token and options into a single options object.
        const newProfileData = await callApi('/user-profile', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: JSON.stringify({ full_name: user.name || 'New User', plan: 'Starter' })
        });
        if (newProfileData && newProfileData.success && newProfileData.user) {
          setProfile(newProfileData.user);
        } else {
          setProfile(null);
        }
      } catch (createError) {
        console.error("Failed to self-heal and create user profile:", createError);
        setProfile(null);
      }
    } else {
      console.error("Error fetching user profile:", error);
      setProfile(null);
    }
    } finally {
      setLoadingProfile(false);
    }
  }, [isAuthenticated, user, getAccessToken]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const updateUserPlan = useCallback(async (planName: string) => {
    if (!isAuthenticated) {
        return { error: new Error("User not authenticated.") };
    }
    try {
        const accessToken = await getAccessToken();
        // FIX: Combine access token and options into a single options object.
        await callApi('/user-profile', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({ plan: planName }),
        });

        await fetchUserProfile(); // Refresh profile after update
        return { error: null };
    } catch (error: any) {
        console.error("Error updating user plan:", error);
        return { error };
    }
  }, [isAuthenticated, getAccessToken, fetchUserProfile]);

  const login = () => {
    // Clear any existing session data before login
    localStorage.clear();
    sessionStorage.clear();
    loginWithRedirect();
  };

  const signup = () => {
    // Clear any existing session data before signup
    localStorage.clear();
    sessionStorage.clear();
    loginWithRedirect({
      // Correct syntax for @auth0/auth0-react v2+
      authorizationParams: {
        screen_hint: 'signup',
      }
    });
  };

  // Correct syntax for @auth0/auth0-react v2+
  const logout = () => {
    // Clear ALL storage to prevent auto-login
    localStorage.clear();
    sessionStorage.clear();
    // Also clear Auth0 cookies/cache
    auth0Logout({
      logoutParams: {
        returnTo: redirectUri,
        // Force re-authentication on next login
        federated: true
      }
    });
  };

  const plan = profile?.plan?.toLowerCase() || 'starter';
  const isStarter = plan === "starter";
  const isCreator = plan === "creator";
  const isPro = plan === "pro";
  const isBusiness = plan === "business";

  const canDeploy = !isStarter;
  const canUseCustomDomain = isPro || isBusiness;
  const canDeployMobile = isPro || isBusiness;
  
  const getProjectLimit = () => {
    switch(plan) {
        case 'business': return 10;
        case 'pro': return 3;
        case 'creator': return 1;
        default: return 0; // Starter plan has 0 live projects
    }
  };
  const maxLiveProjects = getProjectLimit();
  
  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading: isLoading || loadingProfile,
    login,
    signup,
    logout,
    getAccessToken,
    profile,
    loadingProfile,
    refreshProfile: fetchUserProfile,
    updateUserPlan,
    isStarter,
    isCreator,
    isPro,
    isBusiness,
    canDeploy,
    canUseCustomDomain,
    canDeployMobile,
    maxLiveProjects,
    isGithubConnected,
    githubUser,
    connectGithub,
    disconnectGithub,
    createGithubRepo,
    pushToGithub,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const onRedirectCallback = () => {
    // This is where you could redirect users to a specific page after login,
    // but the app's internal navigation logic handles this already.
  };
  
  if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID) {
    const currentUrl = window.location.origin;
    return (
        <div style={{ padding: '20px', backgroundColor: '#ffcdd2', color: '#c62828', textAlign: 'left', fontFamily: 'monospace' }}>
            <h1>Auth0 Configuration Error</h1>
            <p>Auth0 Domain or Client ID is missing in <code>context/AuthContext.tsx</code>.</p>
            <hr style={{margin: '20px 0', borderColor: '#ef9a9a'}} />
            <h2>DEBUGGING HELP FOR URL MISMATCH:</h2>
            <p>The current application URL is: <strong>{currentUrl}</strong></p>
            <p>For Auth0 to work, you MUST add this exact URL to the following fields in your Auth0 Application Settings:</p>
            <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                <li>Allowed Callback URLs</li>
                <li>Allowed Logout URLs</li>
                <li>Allowed Web Origins</li>
            </ul>
        </div>
    );
  }

  return (
    // FIX: Corrected typo from Auth0_Provider to Auth0Provider.
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: AUTH0_AUDIENCE,
        scope: "openid profile email" // Standard scopes to get user profile info
      }}
      onRedirectCallback={onRedirectCallback}
      // FIX: Store tokens in memory (not localStorage) for better mobile compatibility
      // This prevents auto-login on mobile and requires explicit login each time
      cacheLocation="memory"
      // Disable refresh tokens to require re-authentication
      useRefreshTokens={false}
    >
      <AuthProviderInternal>{children}</AuthProviderInternal>
    </Auth0Provider>
  );
};


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};